import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { useBackendAuthenticated } from "@/hooks/useBackendAuthenticated";

const PatientDiaryDTOSchema = z.object({
  id: z.string().uuid(),
  patientId: z.string().uuid(),
  doctorId: z.string().uuid(),
  doctorName: z.string().nullable().optional(),
  title: z.string(),
  content: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type PatientDiaryDTO = z.infer<typeof PatientDiaryDTOSchema>;

const DiariesResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(PatientDiaryDTOSchema),
});

const DiaryResponseSchema = z.object({
  success: z.boolean(),
  data: PatientDiaryDTOSchema,
});

export function usePatientDiaries(patientId?: string) {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["diaries", patientId],
    queryFn: async () => {
      const json = await backend({ method: "GET", url: `/api/v1/patients/${patientId}/diaries` });
      return DiariesResponseSchema.parse(json).data;
    },
    enabled: !!patientId,
  });
}

export function useCreateDiary() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ patientId, title, content }: { patientId: string; title: string; content: string }) => {
      const json = await backend({ method: "POST", url: `/api/v1/patients/${patientId}/diaries`, data: { title, content } });
      return DiaryResponseSchema.parse(json).data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["diaries", data.patientId] });
    },
  });
}

export function useUpdateDiary() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      patientId,
      ...body
    }: { id: string; patientId: string; title?: string; content?: string }) => {
      const json = await backend({ method: "PATCH", url: `/api/v1/patients/diaries/${id}`, data: body });
      return { ...DiaryResponseSchema.parse(json).data, patientId };
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["diaries", data.patientId] });
    },
  });
}

export function useDeleteDiary() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patientId }: { id: string; patientId: string }) => {
      await backend({ method: "DELETE", url: `/api/v1/patients/diaries/${id}` });
      return { patientId };
    },
    onSuccess: ({ patientId }) => {
      void queryClient.invalidateQueries({ queryKey: ["diaries", patientId] });
    },
  });
}
